import { SortingState, ColumnFiltersState } from "@tanstack/react-table";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import { app_modules } from "helpers/apps_modules_list";
import { useEffect, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import TableContainer2 from "Common/Tables/ObjectFiltersTable/TableContainer2";
import { fetchWarehouseWiseStock } from "slices/Inventory/Inventory/Warehouse_Wise_Stock/thunk";
import { fetchLatestPermissions } from "slices/auth/login/thunk";
import { api } from "helpers/api_url";

type Props = {
  loading: boolean;
  pageIndex: number;
  pageSize: number;
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
  filterValue: any[];
  search: string;
  warehouseIdSearch: string;
  sortField: string;
  sortDirection: "asc" | "desc" | "";
  setWarehouseIdSearch: React.Dispatch<React.SetStateAction<string>>;
  setPageIndex: (index: number) => void;
  setPageSize: (size: number) => void;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  setFilterValue: (filters: any[]) => void;
};

const WarehouseWiseStock: React.FC<Props> = ({
  loading,
  pageIndex,
  pageSize,
  sorting,
  columnFilters,
  filterValue,
  search,
  warehouseIdSearch,
  sortField,
  sortDirection,
  setPageIndex,
  setPageSize,
  setSorting,
  setColumnFilters,
  setFilterValue,
}) => {
  document.title = "Warehouse Wise Stock";

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  // -------------------------------------------------------------------
  // ✅ Select Warehouse Wise Stock Data
  // -------------------------------------------------------------------
  const selectWarehouseWiseStock = createSelector(
    (state: any) => state.WareHouseWiseStockReducer,
    (state) => ({
      list: state?.data,
      rowCount: state.totalRecords,
      tableLoading: state.loading,
      error: state.error,
    })
  );

  const { list, rowCount, error, tableLoading } = useSelector(
    selectWarehouseWiseStock
  );

  // -------------------------------------------------------------------
  // 🔥 Fetch Warehouse Wise Stock
  // -------------------------------------------------------------------
  useEffect(() => {
    if (warehouseIdSearch) {
      dispatch(
        fetchWarehouseWiseStock({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? (sortDirection as "asc" | "desc") : "",
          search,
          filters: filterValue,
          warehouse_id: warehouseIdSearch,
        })
      );
    }
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    filterValue,
    warehouseIdSearch,
  ]);

  // -------------------------------------------------------------------
  // 🔐 Fetch Permissions
  // -------------------------------------------------------------------
  useEffect(() => {
    dispatch(fetchLatestPermissions());
  }, [dispatch]);

  // -------------------------------------------------------------------
  // 🎯 Action Column
  // -------------------------------------------------------------------
  const DynamicAction = [
    {
      header: "Actions",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <ul className="d-flex gap-2 list-unstyled mb-0">
          <li>
            <button
              className="btn btn-sm"
              style={{ backgroundColor: "#3969E7", color: "#FFF" }}
              onClick={() =>
                navigate(
                  `/view-warehousewisestockdetails/${row.original.master_item_id}/${warehouseIdSearch}`
                )
              }
            >
              View
            </button>
          </li>
        </ul>
      ),
    },
  ];

  // -------------------------------------------------------------------
  // 📌 Table Columns
  // -------------------------------------------------------------------
  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row, table }: any) => {
          const pIndex = table.getState().pagination.pageIndex;
          const pSize = table.getState().pagination.pageSize;
          return pIndex * pSize + row.index + 1;
        },
      },
      {
        header: "Item Name",
        accessorKey: "item_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown",
        filterFetchUrl: `${api.Inventory.InStock.StockOverView.dropdown}/item_name?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: any) => row.original?.item_name || "-",
      },

      {
        header: "Category",
        accessorKey: "category_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown",
        filterFetchUrl: `${api.Inventory.InStock.StockOverView.dropdown}/category_name?warehouse_id=${warehouseIdSearch}`,
      },

      {
        header: "Sub Category",
        accessorKey: "sub_category_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown",
        filterFetchUrl: `${api.Inventory.InStock.StockOverView.dropdown}/sub_category_name?warehouse_id=${warehouseIdSearch}`,
      },

      {
        header: "Unit",
        accessorKey: "unit_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown",
        filterFetchUrl: `${api.Inventory.InStock.StockOverView.dropdown}/unit_name?warehouse_id=${warehouseIdSearch}`,
      },

      {
        header: "Available Quantity",
        accessorKey: "total_available_quantity",
        enableSorting: false,
        enableColumnFilter: false,
      },

      ...DynamicAction,
    ],
    [dispatch, list]
  );

  // -------------------------------------------------------------------
  // JSX
  // -------------------------------------------------------------------
  return (
    <Row id="warehouseWiseStock">
      <Col lg={12}>
        <Card>
          <Card.Body className="mt-3">
            <LoaderErrorWrapper loading={loading || tableLoading} error={error}>
              <TableContainer2
                columns={columns}
                data={list}
                totalRecords={rowCount}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                colValue={setFilterValue}
                sorting={sorting}
                onSortingChange={setSorting}
                isBordered={false}
                isPagination={true}
                isGlobalFilter={false}
                SearchPlaceholder="Search..."
                manualSorting={true}
              />
            </LoaderErrorWrapper>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default WarehouseWiseStock;
